import { Component, OnInit, OnDestroy, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { PaymentService } from 'app/modules/payment/payment.service';
import { OrganisationService } from 'app/core/organisation/organisation.service';
import { UserService } from 'app/core/user/user.service';

export interface SubscriptionWarningData {
    daysUntilExpiry: number;
    isExpired: boolean;
    subscriptionEndDate: Date;
    customerPortalUrl?: string;
    isTrial?: boolean;
    stripeStatus?: string;
}

@Component({
    selector: 'subscription-warning',
    templateUrl: './subscription-warning.component.html',
    styleUrls: ['./subscription-warning.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatButtonModule,
        MatIconModule,
        MatTooltipModule,
    ],
    // changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionWarningComponent implements OnInit, OnDestroy {
    @Input() showInLayout: boolean = true;
    
    warningData: SubscriptionWarningData | null = null;
    isLoading: boolean = false;
    isOwner: boolean = false;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(
        private _paymentService: PaymentService,
        private _organisationService: OrganisationService,
        private _router: Router,
        private _userService: UserService
    ) {}

    ngOnInit(): void {
        this.loadSubscriptionData();
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    private loadSubscriptionData(): void {
        this.isLoading = true;
        
        this._organisationService.currentOrganisation$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(organisation => {
                if (!organisation?.uuid) {
                    this.isLoading = false;
                    return;
                }

                const user = this._userService.user;
                // if (user?.uid !== organisation.userUid) {
                //     this.isLoading = false;
                //     return;
                // }
                // Load subscription data and customer portal URL in parallel
                const subscriptionRequest = this._paymentService.getSubscriptionActive(organisation.uuid);
                // const customerPortalRequest = this._paymentService.getCustomerPortalLink(organisation.uuid);

                subscriptionRequest
                    .pipe(takeUntil(this._unsubscribeAll))
                    .subscribe({
                        next: (response) => {
                            this.isOwner = user?.uid === organisation.userUid;
                            this.processSubscriptionData(response.data);
                            
                            // Load customer portal URL if we have a subscription
                            // if (response.data?.system) {
                                // customerPortalRequest
                                //     .pipe(takeUntil(this._unsubscribeAll))
                                //     .subscribe({
                                //         next: (portalResponse) => {
                                //             if (portalResponse?.data?.url) {
                                //                 this.warningData = {
                                //                     ...this.warningData,
                                //                     customerPortalUrl: portalResponse.data.url
                                //                 };
                                //             }
                                //         },
                                //         error: (error) => {
                                //             console.error('Error loading customer portal URL:', error);
                                //         }
                                //     });
                            // }
                            
                            this.isLoading = false;
                        },
                        error: (error) => {
                            console.error('Error loading subscription data:', error);
                            this.isLoading = false;
                        }
                    });
            });
    }

    private processSubscriptionData(data: any): void {
        if (!data?.system) {
            this.warningData = null;
            return;
        }

        const subscription = data.system;
        // console.log('Subscription data:', subscription);
        
        // Handle trialing status separately
        if (subscription.stripe_status === 'trialing') {
            this.handleTrialingSubscription(subscription);
            return;
        }
        
        // Check if we should show a warning based on subscription status
        const shouldShowWarning = this.shouldShowWarningForStatus(subscription.stripe_status);
        // console.log('Should show warning:', shouldShowWarning);
        // console.log('warningData:', this.warningData);
        if (shouldShowWarning) {
            this.warningData = {
                daysUntilExpiry: 0, // We'll calculate this if needed
                isExpired: false,
                subscriptionEndDate: new Date(),
                customerPortalUrl: undefined, // Will be set later if available
                isTrial: false,
                stripeStatus: subscription.stripe_status
            };
            
            console.log('Warning data set:', this.warningData);
        } else {
            this.warningData = null;
        }
    }

    private handleTrialingSubscription(subscription: any): void {
        console.log(subscription);
        if (!subscription.trial_end_at) {
            this.warningData = null;
            return;
        }

        const currentDate = new Date();
        const endDate = new Date(subscription.trial_end_at);
        
        if (isNaN(endDate.getTime())) {
            this.warningData = null;
            return;
        }

        // Calculate days until expiry more accurately
        const timeDiff = endDate.getTime() - currentDate.getTime();
        const daysUntilExpiry = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
        const isExpired = timeDiff < 0;
        
        // console.log('Time difference (ms):', timeDiff);
        // console.log('Days until expiry:', daysUntilExpiry);
        
        // Only show warning if trial is expired or has 3 days or fewer remaining
        if (isExpired || daysUntilExpiry <= 3) {
            this.warningData = {
                daysUntilExpiry: Math.max(0, daysUntilExpiry),
                isExpired: isExpired,
                subscriptionEndDate: endDate,
                customerPortalUrl: undefined,
                isTrial: true,
                stripeStatus: 'trialing'
            };
            console.log('Trial warning data set:', this.warningData);
        } else {
            this.warningData = null;
        }
    }

    private shouldShowWarningForStatus(status: string): boolean {
        // Show warnings for these statuses (based on pricing component logic)
        // Exclude 'active' status as it doesn't need warnings
        const warningStatuses = [
            'incomplete', 
            'past_due',
            'unpaid',
            'paused',
            'not_found'
        ];
        
        // For trialing status, we'll check the days remaining in processSubscriptionData
        if (status === 'trialing') {
            return false; // We'll handle this separately
        }
        
        return warningStatuses.includes(status);
    }

    getWarningMessage(): string {
        if (!this.warningData) {
            return 'Subscription status unavailable.';
        }

        const status = this.warningData.stripeStatus;
        switch (status) {
            case 'trialing':
                if (this.warningData.isExpired) {
                    return `Your trial expired ${this.warningData.daysUntilExpiry} day${this.warningData.daysUntilExpiry !== 1 ? 's' : ''} ago.`;
                } else if (this.warningData.daysUntilExpiry === 0) {
                    return 'Your trial expires today.';
                } else if (this.warningData.daysUntilExpiry === 1) {
                    return 'Your trial expires tomorrow.';
                } else if (this.warningData.daysUntilExpiry <= 3 && this.warningData.daysUntilExpiry > 1) {
                    return `Your trial expires in ${this.warningData.daysUntilExpiry} days.`;
                }
                return 'Your trial is active.';
            case 'incomplete':
                return 'Your subscription requires a successful payment within 23 hours to activate.';
            case 'past_due':
                return 'Payment on your latest invoice has failed. Please pay the most recent invoice to reactivate your subscription.';
            case 'unpaid':
                return 'Your latest invoice hasn\'t been paid and access to services has been revoked.';
            case 'paused':
                return 'Your subscription has been paused due to payment issues.';
            case 'not_found':
                return 'No active subscription found. Subscribe to access all features.';
            default:
                return 'Subscription status requires attention.';
        }
    }

    getWarningClass(): string {
        if (!this.warningData) return '';

        const status = this.warningData.stripeStatus;
        
        switch (status) {
            case 'trialing':
                return 'bg-blue-50 border-blue-200 text-blue-800';
            case 'incomplete':
                return 'bg-yellow-50 border-yellow-200 text-yellow-800';
            case 'past_due':
            case 'unpaid':
            case 'not_found':
                return 'bg-red-50 border-red-200 text-red-800';
            case 'paused':
                return 'bg-orange-50 border-orange-200 text-orange-800';
            default:
                return 'bg-yellow-50 border-yellow-200 text-yellow-800';
        }
    }

    getIconClass(): string {
        if (!this.warningData) return '';

        const status = this.warningData.stripeStatus;
        
        switch (status) {
            case 'trialing':
                return 'text-blue-600';
            case 'incomplete':
                return 'text-yellow-600';
            case 'past_due':
            case 'unpaid':
            case 'not_found':
                return 'text-red-600';
            case 'paused':
                return 'text-orange-600';
            default:
                return 'text-yellow-600';
        }
    }

    getButtonClass(): string {
        if (!this.warningData) return '';

        const status = this.warningData.stripeStatus;
        
        switch (status) {
            case 'trialing':
                return 'bg-blue-600 hover:bg-blue-700 text-white';
            case 'incomplete':
                return 'bg-yellow-600 hover:bg-yellow-700 text-white';
            case 'past_due':
            case 'unpaid':
            case 'not_found':
                return 'bg-red-600 hover:bg-red-700 text-white';
            case 'paused':
                return 'bg-orange-600 hover:bg-orange-700 text-white';
            default:
                return 'bg-yellow-600 hover:bg-yellow-700 text-white';
        }
    }

    onRenewSubscription(): void {
        const status = this.warningData?.stripeStatus;
        
        // For not_found and past_due, always redirect to pricing page
        if (status === 'not_found' || status === 'past_due' || status === 'incomplete' || status === 'unpaid') {
            const currentOrg = this._organisationService.currentOrganisation;
            if (currentOrg) {
                this._router.navigate([`/${currentOrg.slug}/settings/pricing`]);
            }
            return;
        }
        
        // For other statuses, try customer portal first, then fallback to pricing
        if (this.warningData?.customerPortalUrl) {
            window.open(this.warningData.customerPortalUrl, '_blank');
        } else {
            // Fallback to pricing page
            const currentOrg = this._organisationService.currentOrganisation;
            if (currentOrg) {
                this._router.navigate([`/${currentOrg.slug}/settings/pricing`]);
            }
        }
    }

    getButtonText(): string {
        if (!this.warningData) return 'Renew';
        
        const status = this.warningData.stripeStatus;
        
        // switch (status) {
        //     case 'trialing':
        //         return 'Add Payment Method';
        //     case 'incomplete':
        //         return 'Complete Payment';
        //     case 'past_due':
        //     case 'unpaid':
        //         return 'Pay Outstanding Invoice';
        //     case 'paused':
        //         return 'Update Payment Method';
        //     case 'not_found':
        //         return 'Subscribe Now';
        //     default:
        //         return 'Manage Subscription';
        // }
        return 'Manage Subscription';
    }

    getButtonIcon(): string {
        if (!this.warningData) return 'refresh';
        
        const status = this.warningData.stripeStatus;
        
        switch (status) {
            case 'trialing':
                return 'credit_card';
            case 'incomplete':
                return 'payment';
            case 'past_due':
            case 'unpaid':
                return 'receipt';
            case 'paused':
                return 'credit_card';
            case 'not_found':
                return 'add_shopping_cart';
            default:
                return 'account_circle';
        }
    }

    onDismiss(): void {
        this.warningData = null;
    }
} 